import { useCallback } from "react";

import { useSelector } from "react-redux";
import { dispatch, getState } from "../../state/store";
import {
    resetUsers,
    setGroupsConfigs,
    setUsersError,
    setUsersLoading,
    setUsersRefresh
  } from "../../state/ducks";
  import { selectGroupsConfigs, selectUsers } from "../../state/selectors";
  

import { strCode } from "../../consts";
import {
  adminUserService,
  appUserService,
  log,
  notificationService,
  t,
  usersService
} from "../../services";

// If app user not admin, save all users groups to state for displaying on user editing
const saveUsersGroups = () => {
  const usersFromState = selectUsers()(getState()) ?? [];
  const groupsConfigs = selectGroupsConfigs()(getState()) ?? [];

  const usersGroups = usersFromState.reduce((accum, curr) => {
    const result = { ...accum };

    curr.groups.forEach((group) => {
      if (group.appType in result && !result[group.appType].includes(group.name)) {
        result[group.appType].push(group.name);
      } else {
        result[group.appType] = [group.name];
      }
    });

    return result;
  }, {});

  // Merge users groups with already fetched and store only uniq values
  Object.keys(usersGroups).forEach((appType) => {
    dispatch(
      setGroupsConfigs({
        appType,
        groups: [...new Set([...usersGroups[appType], ...(groupsConfigs[appType] || [])])]
      })
    );
  });
};

export const useFetchUsers = () => {
  const users = useSelector((store) => store.users?.users);
  const loading = useSelector((store) => store.users?.loading);
  const refresh = useSelector((store) => store.users?.refresh);

  const fetchCallback = useCallback(
    async (isRefresh, assignments = null) => {
      dispatch(setUsersError(null));

      if ((users !== null && !isRefresh) || loading) {
        return;
      }

      dispatch(setUsersLoading(true));

      try {
        dispatch(resetUsers());
        const requests = [usersService.fetchUsers(assignments)];

        if (appUserService.isAdmin()) {
          requests.push(adminUserService.fetchAdminUsers(assignments));
        }

        await Promise.all(requests);

        if (!appUserService.isAdmin()) {
          saveUsersGroups();
        }
      } catch (err) {
        dispatch(setUsersError(err));
        log.error("UserManagementPage: Failed to fetch users", err);
        notificationService.showError({
          title: t(strCode.FETCH_USERS_ERROR),
          message: err?.response?.data?.message ? err.response.data.message : err?.toString()
        });
      } finally {
        dispatch(setUsersLoading(false));
        dispatch(setUsersRefresh(false));
      }
    },
    [refresh, users, loading]
  );

  return { fetchCallback, data: users, loading, refresh };
};
